import {
  Component,
  destroyPlatform,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import * as L from "leaflet";
import {icon, Marker, marker} from "leaflet";
import 'ceotr-web-common/helpers/extensions';
import 'ceotr-web-common/helpers/styles';
import {roundTo} from 'ceotr-web-common/helpers/math'
import {CoordinatesService} from "../coordinates.service";
import * as moment from 'moment';
import * as $ from 'jquery';
import {ReplaySubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {LatLng} from "leaflet";
import {LayerGroup} from "leaflet";
import {Layer} from "leaflet";
import * as esri from 'esri-leaflet';
import {TimezoneService, Timezone} from "../timezone.service";
import { NamedRoutesService } from "ng-named-routes";

@Component({
  selector: 'app-glider-map',
  templateUrl: './glider-map.component.html',
  styleUrls: ['./glider-map.component.scss']
})
export class GliderMapComponent implements OnInit, OnChanges {

  @Input('glider-groups') gliderGroups;
  @Input() selected;
  @Input() highlighted;
  @Input() single;
  @Input('map-title') mapTitle;
  @Input('show-number') showNumber = true;
  @Input('smooth-factor') smoothFactor = 1;
  @Input() id;

  @Output('leafletMapReady') mapReady = new EventEmitter<L.Map>();

  map: ReplaySubject<L.Map> = new ReplaySubject(1);

  mapActive = false; // certain properties cannot be changed once set, so this is only set once outputMap is set up
  timezone: Timezone;

  showLegend = false;

  halifax = { // Default center if no missions
    lat: 45.29034662473613,
    lng: -63.72159486087429,
    zoom: 6
  };
  defaultCenter = this.halifax;
  defaultWeight = 3;
  weightMultiplier = 1.5;
  defaultPathOpacity = .3;
  defaultLocationOpacity = .5;
  zoomedIn = 9;

  // this map contains named pointers to items in outputMap
  middleMap: {
    baselayers: Array<Layer>,
    overlays: Array<Layer>,
    markers: { [key: string]: L.Marker; },
    paths: { [key: string]: L.Polyline; },
  } = {
    baselayers: [],
    overlays: [],
    markers: {}, // an object {deploymentNumber: Marker}
    paths: {}, // an object {deploymentNumber: Path}
  };

  outputMap: {
    selectedCenter: LatLng | null,
    leafletOptions: object,
    leafletZoom: number,
  } = {
    selectedCenter: null,
    leafletOptions: {},
    leafletZoom: 5,
  };

  oldSelected = null;
  oldHighlighted = null;

  nextCenter: { lat: number, lng: number, zoom: number } | null;
  gliders: Glider[];

  inputMap: {
    paths: object,
    markers: object,
    layers: object,
    selectedCenter: { lat: number, lng: number, zoom: number } | null
  } = {
    paths: {},
    markers: {},
    layers: {},
    selectedCenter: null, // once non-null, renders map
  };

  //region Icons
  slocumGliderIcon = new L.Icon({
    iconUrl: '/gliders_website/static/images/icons/slocum-icon.png',
    iconSize: [20, 20],
    iconAnchor: [10, 5],
    popupAnchor: [0, 0]
  });

  waveGliderIcon = new L.Icon({
    iconUrl: '/gliders_website/static/images/icons/wave-icon.png',
    iconSize: [20, 20],
    iconAnchor: [10, 5],
    popupAnchor: [0, 0]
  });

  waypointPreviousIcon = new L.Icon({
    iconUrl: '/gliders_website/static/images/icons/cross-hairs.png',
    iconSize: [30, 30],
    iconAnchor: [15, 5],
    popupAnchor: [0, 0]
  });

  waypointNextIcon = this.waypointPreviousIcon;

  redDotIcon = new L.Icon({
    iconUrl: '/gliders_website/static/images/icons/reddot.png',
    iconSize: [6, 6],
    iconAnchor: [3, 1],
    popupAnchor: [0, 0]
  });

  iconUrl = '/gliders_website/static/images/icons/cross-hairs_highlight.png';

  layer = marker([46.879966, -121.726909], {
    icon: icon({
      iconSize: [25, 41],
      iconAnchor: [13, 41],
      iconUrl: 'assets/marker-icon.png',
      shadowUrl: 'assets/marker-shadow.png'
    })
  });

  constructor(private coordinatesService: CoordinatesService, private timezoneService: TimezoneService,
              private namedRoutesService: NamedRoutesService) {
    this.timezone = timezoneService.timezone;
  }

  ngOnInit() {
    this.highlighted = this.highlighted || null;
    this.selected = this.selected || null;
    this.showLegend = !this.single;
    this.smoothFactor = this.smoothFactor || 1;

    if (this.showNumber === undefined) {
      this.showNumber = true;
    }

    if (!this.mapTitle) {
      this.mapTitle = 'Glider Traces';
    }

    this.initMarkers();
    this.initPaths();
    //this.middleMap.baselayers = new LayerGroup(Object.values(this.layers['baselayers']).map(UiLeafletBridgeComponent.esriOrLayerGroup));
    //this.middleMap.overlays = new LayerGroup(Object.values(this.layers['overlays']).map(UiLeafletBridgeComponent.esriOrLayerGroup));
    this.middleMap.baselayers = Object.values(this.inputMap.layers['baselayers']).map(GliderMapComponent.esriOrLayerGroup);
    this.middleMap.overlays = Object.values(this.inputMap.layers['overlays']).map(GliderMapComponent.esriOrLayerGroup);
    this.outputMap.leafletZoom = 5;
    this.outputMap.leafletOptions = {};
    this.mapActive = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gliderGroups']) {
      this.initMapData();
      this.middleMap.markers = {};
      this.middleMap.paths = {};
      this.initPaths();
      this.initMarkers();
    } else {
      // handle changes to paths
      if (changes['paths']) {
        this.initPaths();
      }
      // handle changes to markers
      if (changes['markers']) {
        this.initMarkers();
      }
    }

    const selected = changes['selected'];
    if (selected && selected.currentValue) {
      this.legendSelect(selected.currentValue);
    }

    // handle highlighted path change
    const highlight = changes['highlighted'];
    if (highlight) {
      this.setHighlighted(highlight.previousValue, highlight.currentValue);
    }
    const select = changes['selected'];
    if (select) {
      this.setSelected(select.previousValue, select.currentValue);
    }
  }

  onMapReady(map: L.Map) {
    this.map.next(map);
    this.mapReady.emit(map);
  }


  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  updateMapHeight() {
    // Inline dimensions to avoid blurry map, dynamic height control for different devices
    const idealMapHeight = window.innerHeight < 500 ? window.innerHeight - 30 : 500;
    $('app-glider-map').css('height', idealMapHeight + 'px');
    $('app-glider-map leaflet ').css('height', idealMapHeight + 'px');
    $('.map-wrapper').css('height', idealMapHeight + 'px');
    $('.glider-map-container').css('height', idealMapHeight + 'px');
    $('.angular-leaflet-map').css('height', idealMapHeight + 'px');
    $('.angular-leaflet-map [ng-transclude]').css('height', idealMapHeight + 'px');
    $('.select-pad').css('maxHeight', (idealMapHeight - 100) + 'px');
    $('.select-pad .current-list').css('maxHeight', (idealMapHeight - 166) + 'px');
  }

  initMapData() {
    const vm = this;
    let pathCount = 0;

    this.inputMap.layers = {
      baselayers: {
        oceans: {
          name: "Oceans",
          type: "agsBase",
          layer: "Oceans",
          visible: true,
          layerParams: {
            showOnSelector: false
          }
        }
      },
      overlays: {
        oceanLabels: {
          name: "Ocean Labels",
          type: "agsBase",
          layer: "OceansLabels",
          visible: true
        },
        waypoints: {
          name: 'Waypoint Markers',
          type: 'group',
          visible: true
        },
        locations: {
          name: 'Glider Markers',
          type: 'group',
          visible: true
        }
      }
    };

    // @ts-ignore
    const selected = Array.indexOfByProperty(this.gliderGroups, this.selected, 'deployment_number');
    if (selected < 0) {
      this.selected = null;
    }

    this.gliders = [];
    this.inputMap.paths = {}; // reset paths
    this.inputMap.markers = {}; // reset markers
    this.gliderGroups.forEach(function (group) {

      const colorPalette: any = window['ColorPalette']; // todo where does this come from??
      const nextColour: any = colorPalette.distinctList[pathCount++ % colorPalette.distinctList.length];
      let coordinate: { lat: number, lng: number }[];
      if (group.info && group.info.coordinates) {
        coordinate = group.info.coordinates;
      } else {
        group.info = {boundary: {}, center: {lng: null, lat: null}, coordinate: [], coordinates: []};
        coordinate = [];
      }
      const glider = {
        deploymentNumber: group.deployment_number,
        platformName: group.platform_name,
        center: vm.defaultCenter,
        path: {
          defaultColor: nextColour,
          weight: vm.defaultWeight,
          latlngs: coordinate,
          opacity: vm.defaultPathOpacity,
          color: nextColour,
          smoothFactor: vm.smoothFactor
        }
      };
      vm.gliders.push(glider);

      if (!glider.path.latlngs) {
        console.debug('No coordinates for #' + group.deployment_number + ', skipping');
      } else { // perhaps some other sanity checks and logs for incomplete data
        vm.inputMap.paths[vm.getLeafletSafeKey(glider.deploymentNumber)] = glider.path;

        //region Waypoint icons
        if (group.status_info && group.status_info.waypoints) {

          const waypoints = group.status_info.waypoints;

          const lastWptStr = vm.coordinatesService.format(waypoints.previous.lat, waypoints.previous.lng);
          const nextWptStr = vm.coordinatesService.format(waypoints.next.lat, waypoints.next.lng);

          vm.inputMap.markers[vm.getLeafletSafeKey('waypointPrevious_' + group.deployment_number)] = {
            lat: waypoints.previous.lat,
            lng: waypoints.previous.lng,
            message: '<b>' + group.platform_name + '</b><br/><br/>Last Waypoint: ' + lastWptStr + vm.getMissionDetailsLink(group),
            icon: vm.waypointPreviousIcon,
            draggable: false,
            opacity: .6,
            layer: 'waypoints'
          };

          vm.inputMap.markers[vm.getLeafletSafeKey('waypointNext_' + group.deployment_number)] = {
            lat: waypoints.next.lat,
            lng: waypoints.next.lng,
            message: '<b>' + group.platform_name + '</b><br/><br/>Target waypoint: ' + nextWptStr + vm.getMissionDetailsLink(group),
            icon: vm.waypointNextIcon,
            draggable: false,
            opacity: 1,
            layer: 'waypoints'
          };
        }
        //endregion

        //region Glider / location icon
        let message = '<b>' + group.platform_name + '</b><br/><br/>';
        if (group.status_info) {
          const current = group.status_info.status.current;

          message += 'Glider\'s last surfacing:&nbsp;&nbsp;' + moment(group.status_info.surfaceTime).format('YYYY MMM DD, h:mma') + ' ' + vm.timezone.short + '<br/>' +
            'Position:&nbsp;&nbsp;' + vm.coordinatesService.format(current.gps_lat, current.gps_lon) + '<br/>' +
            'Battery:&nbsp;&nbsp;' + roundTo(group.status_info.battery, 1) + ' V<br/>' +
            'Traveled&nbsp;&nbsp;' + roundTo(group.status_info.distanceFromLastSurfacingKm, 1) + ' km in ' +
            roundTo(group.status_info.timeSinceLastSurfacingHr, 1) + ' hrs (' +
            roundTo(group.status_info.lastSpeedKmph, 1) + ' km/hr)<br/>' +
            'Next waypoint:&nbsp;&nbsp;' + vm.coordinatesService.format(current.c_wpt_lat, current.c_wpt_lon) + '<br/>' +
            'To next wpt:&nbsp;&nbsp;' + roundTo(group.status_info.distanceToNextWaypointKm, 1) + ' km (' +
            roundTo(group.status_info.timeToNextWaypointHr, 1) + ' hours)<br/>' +
            'ETA next wpt:&nbsp;&nbsp;' + moment(group.status_info.nextWaypointEta).format('YYYY MMM D, h:mma') + ' ' + vm.timezone.short;
        } else {
          const startLatLng = group.info.coordinates[0];
          const endLatLng = group.info.coordinates[group.info.coordinates.length - 1];

          message += 'Deployment date: ' + moment(group.startTime).format('YYYY MMM D, h:mma') + ' ' + vm.timezone.short + '<br/>' +
            'Start location: ' + (startLatLng ? vm.coordinatesService.format(startLatLng.lat, startLatLng.lng) : 'N/A') + '<br/>' +
            'End location: ' + (endLatLng ? vm.coordinatesService.format(endLatLng.lat, endLatLng.lng) : '') + '<br/>' +
            'Distance travelled: ' + roundTo(group.travel_distance, 1) + ' km <br/>' +
            'Travel Duration: ' + roundTo(group.travelDuration.days, 0) + ' days'
          ;
        }

        message += vm.getMissionDetailsLink(group);

        let gliderIcon: L.Icon;
        if (group.glider_type.equalsIgnoreCase('slocum')) {
          gliderIcon = vm.slocumGliderIcon;
        } else if (group.glider_type.equalsIgnoreCase('wave')) {
          gliderIcon = vm.waveGliderIcon;
        } else {
          gliderIcon = vm.redDotIcon; // todo is this a good unknown icon? maybe just use slocum?
        }

        if (group.info.lastCoordinates) {
          vm.inputMap.markers[vm.getLeafletSafeKey('location_' + group.deployment_number)] = {
            lat: group.info.lastCoordinates.lat,
            lng: group.info.lastCoordinates.lng,
            message: message,
            icon: gliderIcon,
            draggable: false,
            opacity: vm.defaultLocationOpacity,
            layer: 'locations'
          };
        } else {
          console.debug('No coordinates provided for ' + glider.deploymentNumber + ' ' + glider.platformName)
        }
        //endregion
      }
    });

    vm.initCenterAndGliderPaths();
  }

  getMissionDetailsLink(group) {
    if (this.single) {
      return '<br/><br/>';
    }
    let missionLink = this.namedRoutesService.getRoute('mission', {deploymentNumber: group.deployment_number})
    return '<br/><br/><a href="' + missionLink + '">Mission #' + group.deployment_number + ' details &rsaquo;</a>';
  }

  calculateCenterPoint() {
    let zoomData = this.halifax;
    let pathInfo = this.gliderGroups.map(group => group.info);
    if (pathInfo.length > 0) {
      let centres = pathInfo.map(path => path.center);
      let centreLat = 0;
      let centreLng = 0;
      let centerValueLength = 0;
      centres.forEach(pair => {
        if (pair) {
          centreLat += pair.lat;
          centreLng += pair.lng;
          centerValueLength += 1;
        }
      });
      centreLat /= centerValueLength;
      centreLng /= centerValueLength;

      zoomData = {
        lat: centreLat,
        lng: centreLng,
        zoom: 6,
      }
    }
    return zoomData;
  }

  /**
   * Set the bounds of the map to fit all glider paths. Either uses a provided array of coordinates or
   * uses the coordinates of all glider groups.
   * If halifax==true and mission==null, will zoom to Halifax instead of fitting all paths
   */
  resetBounds(mission?, halifax?: boolean) {
    const vm = this;
    let latlngs: LatLng[];
    if (mission) {
      latlngs = mission.path.latlngs;
    } else if (halifax) {
      latlngs = [ // square around Halifax
        new LatLng(this.halifax.lat - 5, this.halifax.lng - 5),
        new LatLng(this.halifax.lat + 5, this.halifax.lng + 5)];
    } else {
      let pathInfo = this.gliderGroups.map(group => group.info);
      latlngs = [].concat(...pathInfo.map(path => path.coordinates)); // all coordinates in one array
    }
    let lats = latlngs.map(coord => coord.lat);
    let lngs = latlngs.map(coord => coord.lng);

    // find bounds and add aesthetic buffer
    const minLat = this.min(lats);
    const maxLat = this.max(lats);
    const minLng = this.min(lngs);
    const maxLng = this.max(lngs);
    const latBuffer = 0.1 * (maxLat - minLat);
    const lngBuffer = 0.1 * (maxLng - minLng);
    const bounds: [number, number][] = [[minLat - latBuffer, minLng - lngBuffer], [maxLat + latBuffer, maxLng + lngBuffer]];

    this.map.subscribe(
      map => {
        map.options.zoomSnap = 0;
        // check if any coord is NaN or +/- Infinity
        const invalidBounds: boolean = [minLat, minLng, maxLat, maxLng].some(num => (isNaN(num) || num == -Infinity || num == Infinity));
        if (invalidBounds) {
          if (mission) {
            console.error('Invalid bounds for deployments' + mission.deploymentNumber + ', one or more is +/- Infinity');
          } else {
            console.error('Invalid bounds, one or more is +/- Infinity')
          }
          const hfx = vm.halifax;
          map.fitBounds([[hfx.lat - 10, hfx.lat + 10], [hfx.lng - 10, hfx.lng + 10]]);
        } else {
          map.fitBounds(bounds);
        }
      },
      err => {
        console.error(err);
      }
    );
  }

  initCenterAndGliderPaths() {
    // can use this.defaultCenter = this.calculateCenterPoint() to calc based on glider coords
    // but it just shows all of Canada due to a handful of west coast gliders
    this.defaultCenter = this.halifax;

    const gliders: Glider[] = [];
    let selectedGlider: Glider | undefined;

    for (let i = 0; i < this.gliderGroups.length; i++) {
      const gliderGroup = this.gliderGroups[i];
      const glider = this.gliders[i];

      if (glider.deploymentNumber == this.selected) {
        selectedGlider = glider;
      }

      if (gliderGroup.info.center) {
        glider.center = {
          lat: gliderGroup.info.center.lat,
          lng: gliderGroup.info.center.lng,
          zoom: this.zoomedIn
        };
      } else {
        glider.center = {
          lat: this.defaultCenter.lat,
          lng: this.defaultCenter.lng,
          zoom: this.zoomedIn
        };
      }
      gliders.push(glider);
    }
    gliders.sort((a, b) => b['deploymentNumber'] - a['deploymentNumber']);
    this.gliders = gliders;

    // Init / show the map
    this.updateMapHeight();
    this.inputMap.selectedCenter = selectedGlider ? selectedGlider.center : this.defaultCenter;

    // reset zoom to fit all paths
    this.resetBounds(null, true);
    setTimeout(this.updateMapHeight, 500);
  }

  setCenter(value) {
    if (this.nextCenter) {
      value = this.nextCenter;
      this.nextCenter = null;
    }
    if (value) {
      this.inputMap.selectedCenter = value;
    }
  }

  /**
   * Fires when clicking a deployments in the map legend - zoom to its path/marker and open an info popup
   * @param deploymentNumber
   */
  legendSelect(deploymentNumber) {
    this.highlighted = deploymentNumber; // make path thicker and more opaque
    this.selected = deploymentNumber;
    this.scrollPathIntoView(deploymentNumber); // zoom in on path
    this.openPopup(deploymentNumber); // open info popup
  }

  /**
   * Reposition and resize the map view to contain the path corresponding to a particular deployments number
   * @param deploymentNumber deployments number of the path to zoom in on
   */
  scrollPathIntoView(deploymentNumber: number): void {
    if (this.isPathEmpty(deploymentNumber)) {
      console.debug('No path');
      return;
    }
    //const path = this.inputMap.paths[deploymentNumber];
    const mission = Array.getByProperty(this.gliders, deploymentNumber, 'deploymentNumber');
    this.resetBounds(mission);
  }

  /**
   * Returns true if a deployments number has no marker and its path object has an empty coordinates array
   * @param deploymentNumber
   */
  isPathEmpty(deploymentNumber: number): boolean {
    const marker: Marker = this.inputMap.markers['location_' + deploymentNumber];
    const path: Path = this.inputMap.paths[deploymentNumber];
    if (path) {
      return (!marker && path['latlngs'].length == 0);
    } else {
      return true;
    }
  }

  closePopups() {
    return this.map.pipe(map(leafletMap => {
      leafletMap.closePopup();
      return leafletMap;
    }));
  }

  openPopup(deploymentNumber) {
    const inMarker = this.inputMap.markers['location_' + deploymentNumber];
    if(!inMarker) {
      console.debug('No marker');
      return;
    }
    const lat = inMarker.lat;
    const lng = inMarker.lng;
    const title = inMarker.message || '';
    // map guaranteed to have been initialized here
    this.map.subscribe(map => {
      let marker: Marker | undefined;
      // find the actual leaflet marker, since this has the openPopup() function
      map.eachLayer((layer: L.Layer) => {
        if (layer instanceof Marker) {
          if (layer.getLatLng().lat == lat && layer.getLatLng().lng == lng) {
            marker = layer;
          }
        }
      });
      if (marker) {
        L.popup()
          .setLatLng(marker.getLatLng())
          .setContent(title)
          .openOn(map);
      }
    });
  }

  /**
   * Get minimum of an array
   * @param arr
   */
  min(arr: Array<number>): number {
    if (arr.length == 0) {
      return NaN;
    } else if (arr.length == 1) {
      return arr[0];
    }
    return arr.reduce((x, y) => x < y ? x : y);
  }

  /**
   * Get maximum of an array
   * @param arr
   */
  max(arr: Array<number>): number {
    if (arr.length == 0) {
      return NaN;
    } else if (arr.length == 1) {
      return arr[0];
    }
    return arr.reduce((x, y) => x > y ? x : y);
  }

  /**
   * Register listeners to highlight the path on a click or mouseover and unhighlight it on a mouseout
   */
  registerHighlightListeners(trigger, deploymentNumber) {
    trigger.on('mouseover', _ => {
      this.setHighlighted(this.highlighted, deploymentNumber);
    });
    trigger.on('mouseout', _ => {
      if (this.highlighted == deploymentNumber) {
        this.setHighlighted(deploymentNumber, null);
      }
    });
    trigger.on('click', (event, leafletPayload) => {
      this.setSelected(this.selected, deploymentNumber);
      this.openPopup(deploymentNumber);
      this.selected = parseInt(deploymentNumber);
      if (event.name == 'leafletDirectiveMarker.' + this.id + '.click') {
        const coords = this.inputMap.markers[leafletPayload.modelName];
        this.nextCenter = {
          lat: coords.lat,
          lng: coords.lng,
          zoom: this.zoomedIn
        };
      }
      this.scrollSelectionsIntoView();
    });
  }

  /**
   * For each marker defined in `markers`, create a Marker object and store these in `middleMap.markersObject`, then
   * create a LayerGroup out of these objects and store it in `middleMap.markersGroup`
   */
  initMarkers() {
    Object.keys(this.inputMap.markers).forEach(markerName => {
      const marker = this.inputMap.markers[markerName];
      const markerObject = new L.Marker(
        {lat: marker['lat'], lng: marker['lng']},
        {
          icon: marker['icon']
        },
      );
      this.middleMap.markers[markerName] = markerObject;
      // if marker is a glider marker, highlight the path on mouseover and click
      const parts = markerName.split('_');
      const deploymentNumber = parseInt(parts[parts.length - 1]);
      this.registerHighlightListeners(markerObject, deploymentNumber);
    });
  }

  /**
   * For each path defined in `paths`, create a Path object and store these in `middleMap.pathsObject`, then
   * create a LayerGroup out of these objects and store it in `middleMap.pathsGroup`.
   */
  initPaths() {
    Object.keys(this.inputMap.paths).forEach(deploymentNumber => {
      const path = this.inputMap.paths[deploymentNumber];
      const pathObject = new L.Polyline(
        path['latlngs'].map(latlng => new LatLng(latlng['lat'], latlng['lng'])),
        {
          opacity: path['opacity'],
          smoothFactor: path['smoothFactor'],
          weight: path['weight'],
          color: path['color'],
        });
      this.registerHighlightListeners(pathObject, deploymentNumber);
      this.middleMap.paths[deploymentNumber] = pathObject;
    });
  }

  /**
   * Take an object representing a layer. If it has a 'layer' property, it refers to an esri layer type. Otherwise,
   * it should be a blank layergroup.
   * @param layer
   */
  private static esriOrLayerGroup(layer) {
    if (layer['layer']) {
      return esri.basemapLayer(layer['layer']);
    } else {
      return new LayerGroup([]);
    }
  }

  setHighlighted(previousValue, currentValue) {
    if (previousValue) {
      this.highlighted = null;
      if (this.selected != previousValue) { // if it's also selected, don't unhighlight it
        const previous = this.middleMap.paths[previousValue];
        if (previous && previous.getLatLngs().length > 0) {
          previous.setStyle({
            opacity: this.defaultPathOpacity,
            weight: this.defaultWeight,
          });
        }
      }
    }
    if (currentValue) {
      this.highlighted = currentValue;
      const current = this.middleMap.paths[currentValue];
      if (current && current.getLatLngs().length > 0) {
        current.setStyle({
          opacity: 1,
          weight: this.defaultWeight + this.weightMultiplier,
        });
      }
    }
  }

  setSelected(previousValue, currentValue) {
    console.debug('set selected from ' + previousValue + ' to ' + currentValue);
    if (previousValue) {
      this.selected = null;
      const marker: L.Marker = this.middleMap.markers[this.getLeafletSafeKey("location_" + previousValue)];
      if (marker) {
        marker.setOpacity(this.defaultLocationOpacity);
      }
      const path = this.middleMap.paths[previousValue];
      path.setStyle({
        opacity: this.defaultPathOpacity,
        weight: this.defaultWeight,
      });
    }
    if (currentValue) {
      this.setHighlighted(this.highlighted, null); // unhighlight current
      this.selected = currentValue;
      const marker = this.middleMap.markers[this.getLeafletSafeKey("location_" + currentValue)];
      if (marker) {
        marker.openPopup();
        marker.setOpacity(1);
      }
      const path = this.middleMap.paths[currentValue];
      path.setStyle({
        opacity: 1,
        weight: this.defaultWeight + this.weightMultiplier,
      });
      this.setCenter(this.nextCenter);
    }
  }

  scrollSelectionsIntoView() {
    if (this.selected && this.showLegend) {
      const $el = $('#legend-deployments-' + this.selected);
      const $legend = $('#' + this.id + ' .current-list');

      // todo can these actually be undefined or is using ! fine?
      const elTop = $el.offset()!.top;
      const legendTop = $legend.offset()!.top;

      $legend[0].scrollTop = $legend[0].scrollTop + elTop - legendTop;
    }
  }

  getLeafletSafeKey(name: string) {
    return ('' + name).replace('-', '_');
  }
}

interface Path {
  defaultColor: Color,
  weight: number,
  latlngs: { lat: number, lng: number }[],
  opacity: number,
  color: Color,
  smoothFactor: number,
}

interface Glider {
  deploymentNumber: number,
  platformName: string,
  center: { lat: number, lng: number, zoom: number },
  path: Path,
}

type Color = any // todo temporary until I figure out where the palette is actually from
